<script lang="ts">
	// Svelte services
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';

	// Helpers
	import { createGuiDeviceId } from 'src/components/Scenes/helpers/utilities_ui_to_scenes';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		devicePropertyToIotsParam,
		iotsParamInterpretator,
	} from 'src/components/Scenes/helpers/math_based/math_param_utils';

	// Components
	import {
		Button,
		Container,
		Modal,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system/index';

	import SelectDevice from './SelectDevice.svelte';
	import SelectDeviceProperty from './SelectDeviceProperty.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let showModal = false;
	export let editingParamName = '';
	export let editingParamValue: string;

	export let onChangeParam = (name: string, value: string) => {};

	// Set the currently selected device by the provided math param value (iots param e.g. "dev:id16:m1")
	$: editingParam = iotsParamToSelectedDevice(editingParamValue);
	$: selectedDevice = editingParam ? { id: editingParam?.id } : null;
	$: selectedProperty = editingParam?.condition || null;
	// Wizard props
	$: isNextDisabled = !selectedDevice?.id || (activeStep === 1 && !selectedProperty);
	$: isLastStepWizard = activeStep === wizardItems.length - 1;

	const _format = get(format);

	// The wizard items for the modal
	$: wizardItems = [
		{
			title: _format('wizard_step_device', { default: 'Device' }),
			active: true,
			disabled: false,
			stepNumber: 1,
			controller: SelectDevice,
		},
		{
			title: _format('wizard_step_device_prop', {
				default: 'Device properties',
			}),
			active: false,
			disabled: !selectedDevice,
			stepNumber: 2,
			controller: SelectDeviceProperty,
		},
	];
	let activeStep = 0;
	const setActiveWizardStep = (index: number) => {
		activeStep = index;
		wizardItems = [
			...wizardItems.map((item, i) => {
				return {
					...item,
					active: i === index,
				};
			}),
		];
	};

	/**
	 * Editing iots param to selected device and property.
	 * @param iotsParam - iots param e.g. "dev:id16:m1"
	 * @returns  Selected device and property (`meter`, `temperature`, etc.)
	 */
	const iotsParamToSelectedDevice = (iotsParam: string): { id: string; condition: string } => {
		if (!iotsParam) return null;
		let { iots_condition, deviceID } = iotsParamInterpretator(iotsParam);
		let device_id = createGuiDeviceId(deviceID);

		if (import.meta.env.DEV) {
			console.log('[Math Param Wizard]: editing: ', iotsParam, ' => ', device_id, iots_condition);
		}
		return { id: device_id, condition: iots_condition };
	};

	const handleSelectDevice = (event: CustomEvent<{ id: string }>) => {
		selectedDevice = event?.detail;
		activeStep = 1; // Move to next step
	};

	const handleSelectProperty = (event: CustomEvent<string>) => {
		selectedProperty = event?.detail;
	};

	const handleSaveButton = () => {
		showModal = false;
		if (!selectedDevice?.id || !selectedProperty) {
			return;
		}
		let iotsParam = devicePropertyToIotsParam(selectedDevice.id, selectedProperty);
		if (import.meta.env.DEV) {
			console.log(
				'[Math Param Wizard]: Set device and property: ',
				selectedDevice,
				selectedProperty,
				iotsParam,
			);
		}

		onChangeParam(editingParamName, iotsParam);
	};
</script>

<Modal
	{showModal}
	on:close={() => (showModal = false)}
	title={$format('scenes_choose_device_math_param', {
		default: 'Choose Math Parameter: {name}',
		values: { name: editingParamName },
	})}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					setActiveWizardStep(i);
				}}
				active={activeStep === i}
				title={item.title}
				disabled={item.disabled}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<Container padding="0" slot="content">
		<WizardContainer activeStepIndex={activeStep} adaptiveHeight>
			{#each wizardItems as item, i (i)}
				<WizardContent class="pt-s pb-s" active={activeStep === i}>
					<svelte:component
						this={item.controller}
						{selectedDevice}
						{selectedProperty}
						on:selectDevice={handleSelectDevice}
						on:selectProperty={handleSelectProperty}
					/>

					<div class="d-flex justify-content-end" slot="bottom">...</div>
				</WizardContent>
			{/each}
		</WizardContainer>
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		{#if activeStep > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => setActiveWizardStep(activeStep - 1)}
				>{capFirstChar($format('button_back'))}</Button
			>
		{:else}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (showModal = false)}>{capFirstChar($format('button_cancel'))}</Button
			>
		{/if}

		{#if !isLastStepWizard}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				rightIcon={'fas fa-chevron-right'}
				responsiveSize={{
					sm: 'm',
				}}
				disabled={isNextDisabled}
				variant="primary"
				on:click={() => setActiveWizardStep(activeStep + 1)}
				>{capFirstChar($format('button_next'))}</Button
			>
		{:else}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				icon={'fas fa-save'}
				disabled={isNextDisabled}
				responsiveSize={{
					sm: 'm',
				}}
				variant={'primary'}
				on:click={handleSaveButton}>{capFirstChar($format('button_apply'))}</Button
			>
		{/if}
	</div>
</Modal>
